import Head from 'next/head'
import { host } from '#/configs'
import api from '#/configs/api'
import { formPriceObject } from '#/containers/PropertyDetail/hooks/usePrice'
import { breadcrumbsZones } from '#/hooks/useBreadcrumbs'
import { commaNumber } from '#/utils/number'
import { viewUrl } from '#/utils/properties'
import { description } from '#/utils/property'
import { hash2MapUrl } from '#/utils/url'

export const metaData = (
  mode: 'Page' | 'Component',
  insightsData: TopHap.PropertyState['insightsData'],
  normalized: TopHap.ExtendedProperty
) => {
  const normalizedUrl = `${host}${viewUrl(normalized.id, normalized.address)}`
  // information for meta tags
  const meta = {
    title: `${normalized.displayAddress}, ${normalized.displayRegion} | TopHap`,
    description: description(normalized, insightsData, true) + 'View more property details, owner info, and environmental data on TopHap.',
    image: `${api.widgetBaseUrl}/api/property/${normalized.id}`,
    url: normalizedUrl,
    geo: normalized.location && {
      position: `${normalized.location[1]};${normalized.location[0]}`,
      placename: `${normalized.address.City}, ${normalized.address.StateOrProvince}`,
      region: `US-${normalized.address.StateOrProvince}`
    },
    twitter: {
      address: {
        street_address: normalized.address.UnparsedAddress,
        city: normalized.address.City,
        state_code: normalized.address.StateOrProvince,
        zip: ''
      },
      info: {
        price: `$${commaNumber(normalized.TransactionAmount)}`,
        beds: normalized.BedsCount,
        baths: normalized.BathsDecimal,
        sqft: normalized.LivingSqft
      }
    }
  }
  const seoImages =
    normalized?.media && normalized?.media?.count > 0 ?
      normalized?.media.photos.slice(0, 3) :
      []
  const seoPropertyType = ['Product']
  switch (normalized.PropertyUse) {
    case 'House':
      seoPropertyType.push('SingleFamilyResidence')
      break
    case 'Condo':
      seoPropertyType.push('Apartment')
      break
    default:
      seoPropertyType.push('Residence')
  }

  const isActive = ['Active', 'Pending'].includes(normalized.TophapStatus)

  const zones = breadcrumbsZones(normalized.zones)
  const zoneLink = (id: string) => {
    const hash = { l: [id] }

    return hash2MapUrl(hash)
  }

  const seoBreadcrumbs = [
    ...zones,
    { id: normalized.id, name: normalized.displayAddress }
  ].map((zone, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Thing',
      '@id': `https://www.tophap.com${zoneLink(zone.id)}`,
      name: zone.name
    }
  }))

  const seoProduct: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': seoPropertyType,
    name: `${normalized.displayAddress}, ${normalized.displayRegion}`,
    description:
      `${normalized.displayAddress}, ${normalized.displayRegion} is a ` +
      (normalized.BedsCount ? `${normalized.BedsCount} bed, ` : '') +
      (normalized.BathsDecimal ? `${normalized.BathsDecimal} bath, ` : '') +
      (normalized.LivingSqft ?
        `${commaNumber(Math.round(normalized.LivingSqft))} Sq. Ft. ` :
        '') +
      (normalized.PropertyUse ?
        `${normalized.PropertyUse} ` :
        '') +
      (normalized.YearBuilt ? `built in ${normalized.YearBuilt} ` : '') +
      (normalized.LotAcres && normalized.LotAcres > 0 ?
        `on a ${Number(normalized.LotAcres).toFixed(3)} acre lot ` :
        '') +
      (normalized.permitsCount ?
        `with ${normalized.permitsCount} construction permits.` :
        '') +
      (normalized.PublicRemarks ?
        ` ${normalized.PublicRemarks.substr(0, 66) + '...'}` :
        ''),
    image: seoImages,
    address: {
      '@type': 'PostalAddress',
      streetAddress: normalized.displayAddress,
      addressLocality: normalized.address.City,
      postalCode: normalized.address.PostalCode,
      addressRegion: normalized.address.StateOrProvince
    },
    geo: normalized.location && {
      '@type': 'GeoCoordinates',
      latitude: normalized.location[1],
      longitude: normalized.location[0]
    }
  }

  if (normalized.Price) {
    // @ts-ignore
    const formatted = formPriceObject(normalized, true)
    seoProduct.offers = {
      '@type': 'Offer',
      url: normalizedUrl,
      price: Number(formatted.Price.toString().replace('$', '').replace(/,/g, '')),
      priceCurrency: 'USD'
    }
  }
  if (seoProduct.offers) {
    if (isActive) {
      // @ts-ignore
      seoProduct.offers.availability = 'https://schema.org/InStock'
    } else {
      // @ts-ignore
      seoProduct.offers.availability = 'https://schema.org/OutOfStock'
    }
  }
  return (
    <Head>
      <title key='title'>{meta.title}</title>
      {mode === 'Page' && (
        <>
          <meta key='thumbnail' content={meta.image} name='thumbnail' />
          <meta
            key='CLASSIFICATION'
            content='Business: Real Estate'
            name='CLASSIFICATION'
          />
          <meta
            key='HOME_URL'
            content='https://www.tophap.com'
            name='HOME_URL'
          />
          <meta key='COPYRIGHT' content='TopHap' name='COPYRIGHT' />
          <meta
            key='CATEGORY'
            content='Business: Real Estate'
            name='CATEGORY'
          />
          <meta key='AUTHOR' content='TopHap' name='AUTHOR' />

          {meta.geo && (
            <>
              <meta
                key='geo.position'
                content={meta.geo.position}
                name='geo.position'
              />
              <meta
                key='geo.placename'
                content={meta.geo.placename}
                name='geo.placename'
              />
              <meta
                key='geo.region'
                content={meta.geo.region}
                name='geo.region'
              />
            </>
          )}

          <meta
            key='description'
            content={meta.description}
            name='description'
          />
          <meta key='twitter:title' content={meta.title} name='twitter:title' />
          <meta
            key='twitter:description'
            content={meta.description}
            name='twitter:description'
          />
          <meta key='twitter:image' content={meta.image} name='twitter:image' />
          <meta
            key='twitter:image:alt'
            content={meta.title}
            name='twitter:image:alt'
          />
          <meta
            key='twitter:text:title'
            content={meta.title}
            name='twitter:text:title'
          />
          <meta
            key='twitter:text:description'
            content={meta.description}
            name='twitter:text:description'
          />
          <meta
            key='twitter:text:description_simple'
            content={meta.description}
            name='twitter:text:description_simple'
          />
          <meta
            key='twitter:image:src'
            content={meta.image}
            name='twitter:image:src'
          />
          <meta
            key='twitter:text:street_address'
            content={meta.twitter.address.street_address}
            name='twitter:text:street_address'
          />
          <meta
            key='twitter:text:city'
            content={meta.twitter.address.city}
            name='twitter:text:city'
          />
          <meta
            key='twitter:text:state_code'
            content={meta.twitter.address.state_code}
            name='twitter:text:state_code'
          />
          <meta
            key='twitter:text:zip'
            content={meta.twitter.address.zip}
            name='twitter:text:zip'
          />
          <meta
            key='twitter:text:price'
            content={meta.twitter.info.price}
            name='twitter:text:price'
          />
          <meta
            key='twitter:text:beds'
            content={String(meta.twitter.info.beds)}
            name='twitter:text:beds'
          />
          <meta
            key='twitter:text:baths'
            content={String(meta.twitter.info.baths)}
            name='twitter:text:baths'
          />
          <meta
            key='twitter:text:sqft'
            content={String(meta.twitter.info.sqft)}
            name='twitter:text:sqft'
          />
          <meta
            key='twitter:url:landing_url'
            content={meta.url}
            name='twitter:url:landing_url'
          />
          <meta key='og:title' content={meta.title} property='og:title' />
          <meta key='og:type' content='property:detail' property='og:type' />
          <meta key='og:url' content={meta.url} property='og:url' />
          <meta key='og:image' content={meta.image} property='og:image' />
          <meta
            key='og:description'
            content={meta.description}
            property='og:description'
          />

          <link key='canonical' href={meta.url} rel='canonical' />
        </>
      )}
      {mode === 'Page' && (
        <script
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seoProduct) }}
          type='application/ld+json'
        />
      )}
      {mode === 'Page' && (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'BreadcrumbList',
              itemListElement: seoBreadcrumbs
            })
          }}
          type='application/ld+json'
        />
      )}
    </Head>
  )
}
